import { useEffect, useRef, useState } from "react";
import { type Identifier } from "ra-core";
import { CreditCard, Landmark, Plus, Send } from "lucide-react";
import { RowButton, TextButton } from "@/components/ui/pressable";
import { AddClientCardDialog } from "@/modules/billing/AddClientCardDialog";
import { cn } from "@/lib/utils";
import {
  chargeMethodTitle,
  type SavedClientPaymentMethod,
} from "@/modules/billing/chargeOnSendTypes";
import { useClientSavedPaymentMethods } from "@/modules/billing/useClientSavedPaymentMethods";

/** Same card saved twice (e.g. through Link): keep the selected, default or authorized one. */
const dedupeMethods = (
  methods: SavedClientPaymentMethod[],
  selectedId: string | null,
) => {
  const rank = (method: SavedClientPaymentMethod) =>
    (method.id === selectedId ? 4 : 0) +
    (method.is_default ? 2 : 0) +
    (method.authorized ? 1 : 0);
  const byKey = new Map<string, SavedClientPaymentMethod>();
  for (const method of methods) {
    const key = [
      method.type,
      method.brand ?? method.bank_name,
      method.last4,
      method.exp_month,
      method.exp_year,
    ].join("|");
    const kept = byKey.get(key);
    if (!kept || rank(method) > rank(kept)) byKey.set(key, method);
  }
  return [...byKey.values()];
};

const methodHint = (method: SavedClientPaymentMethod) => {
  const parts: string[] = [];
  if (method.type === "us_bank_account") {
    parts.push("Bank payment · clears in about 4 business days");
  } else if (method.exp_month && method.exp_year) {
    parts.push(
      `Expires ${String(method.exp_month).padStart(2, "0")}/${String(method.exp_year).slice(-2)}`,
    );
  }
  if (method.authorized) parts.push("Authorized by the client");
  return parts.join(" · ");
};

/**
 * "How will this be paid?" on the last send step: email the invoice (pay
 * link) or charge a saved card / bank account now. `value` is the payment
 * method id, or null to send the invoice. "Add a card" opens a popup to
 * save one by hand and selects it.
 */
export const ChargeOnSendPicker = ({
  companyId,
  contactId,
  value,
  onChange,
  onSelectedMethodChange,
  disabled = false,
  className,
  recipientEmail,
  clientLabel,
}: {
  companyId?: Identifier | null;
  contactId?: Identifier | null;
  /** Billing email: Stripe needs one to save a card. */
  recipientEmail?: string | null;
  clientLabel?: string;
  value: string | null;
  onChange: (paymentMethodId: string | null) => void;
  /** The selected card / bank account (null: send the invoice). */
  onSelectedMethodChange?: (method: SavedClientPaymentMethod | null) => void;
  disabled?: boolean;
  className?: string;
}) => {
  const [addedMethodId, setAddedMethodId] = useState<string | null>(null);
  const { data, refetch } = useClientSavedPaymentMethods({
    companyId,
    contactId,
    includePaymentMethodId: addedMethodId,
  });
  const methods = dedupeMethods(data?.methods ?? [], value);
  const selectedMethod = methods.find((method) => method.id === value) ?? null;
  useEffect(() => {
    onSelectedMethodChange?.(selectedMethod);
  }, [selectedMethod, onSelectedMethodChange]);
  const [addOpen, setAddOpen] = useState(false);

  // Clients with "Charge on send" start on their default card.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current || !data) return;
    seededRef.current = true;
    if (data.charge_on_send) {
      const fallback =
        data.methods.find((m) => m.is_default) ?? data.methods[0];
      if (fallback) onChange(fallback.id);
    }
  }, [data, onChange]);

  if (!companyId && !contactId) return null;

  const option = (params: {
    key: string;
    selected: boolean;
    icon: React.ReactNode;
    title: string;
    hint: string;
    onSelect: () => void;
  }) => (
    <RowButton
      key={params.key}
      layout="none"
      density="none"
      selected={params.selected}
      disabled={disabled}
      onClick={params.onSelect}
      className={cn(
        "flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left",
        params.selected ? "border-primary bg-primary/5" : "hover:bg-muted/40",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-full border",
          params.selected ? "border-primary" : "border-muted-foreground/40",
        )}
      >
        {params.selected ? (
          <span className="size-2 rounded-full bg-primary" />
        ) : null}
      </span>
      <span className="shrink-0 text-muted-foreground">{params.icon}</span>
      <span className="min-w-0">
        <span className="block text-sm font-medium">{params.title}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {params.hint}
        </span>
      </span>
    </RowButton>
  );

  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-xs font-medium text-muted-foreground">
        How will this be paid?
      </p>
      <div className="space-y-2" role="radiogroup">
        {option({
          key: "send",
          selected: value == null,
          icon: <Send className="size-4" />,
          title: "Send invoice",
          hint: "The client pays with the link in the email",
          onSelect: () => onChange(null),
        })}
        {methods.map((method) =>
          option({
            key: method.id,
            selected: value === method.id,
            icon:
              method.type === "us_bank_account" ? (
                <Landmark className="size-4" />
              ) : (
                <CreditCard className="size-4" />
              ),
            title: `Charge ${chargeMethodTitle(method)} now`,
            hint: methodHint(method),
            onSelect: () => onChange(method.id),
          }),
        )}
      </div>
      {recipientEmail ? (
        <TextButton
          tone="accent"
          size="sm"
          disabled={disabled}
          onClick={() => setAddOpen(true)}
        >
          <Plus aria-hidden />
          Add a card
        </TextButton>
      ) : null}
      <AddClientCardDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        companyId={companyId}
        contactId={contactId}
        clientLabel={clientLabel}
        recipientEmail={recipientEmail ?? ""}
        onSaved={async (paymentMethodId) => {
          setAddedMethodId(paymentMethodId);
          await refetch();
          onChange(paymentMethodId);
        }}
      />
    </div>
  );
};
